<template>
    <div class="min-h-screen bg-gray-50">
        <!-- Banner de Homologação -->
        <div v-if="isHomolog" class="fixed top-0 left-0 right-0 z-60 bg-orange-500 border-b border-orange-600 shadow-md">
            <div class="flex items-center justify-center h-8 px-4">
                <svg class="w-4 h-4 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span class="text-xs font-medium text-white tracking-wide">AMBIENTE DE HOMOLOGAÇÃO</span>
            </div>
        </div>

        <div v-if="sidebarOpen" class="fixed inset-0 z-40 lg:hidden" @click="sidebarOpen = false">
            <div class="fixed inset-0 bg-gray-600 bg-opacity-75"></div>
        </div>

        <nav :class="[
            'fixed inset-y-0 left-0 z-50 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0',
            'w-64 lg:w-64 xl:w-72',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full',
            isHomolog ? 'top-8' : 'top-0'
        ]" role="navigation" aria-label="Menu principal">
            <div class="flex flex-col h-full">
                <div class="flex items-center justify-between h-16 px-4 border-b border-gray-200">
                    <div class="flex items-center">
                        <div class="flex-shrink-0">
                            <div class="w-14 h-14 bg-pdca-600 rounded-lg flex items-center justify-center p-1">
                                <img src="/pdca-logo-trinta-anos.png" alt="PDCA"
                                    class="h-full w-full object-contain filter brightness-0 invert" />
                            </div>
                        </div>
                        <div class="ml-3">
                            <h1 class="text-lg font-semibold text-gray-900">Portal do Cliente</h1>
                        </div>
                    </div>
                    <button @click="sidebarOpen = false"
                        class="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                        aria-label="Fechar menu">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div id="main-navigation" class="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
                    <div class="space-y-1">
                        <router-link v-for="item in mainNavigationItems" :key="item.name" :to="item.to" :class="[
                            'sidebar-link group',
                            isActiveRoute(item.name) ? 'sidebar-link-active' : 'sidebar-link-inactive'
                        ]" @click="sidebarOpen = false">
                            <component :is="item.icon" class="w-5 h-5 mr-3 flex-shrink-0" />
                            <span class="truncate">{{ item.label }}</span>
                        </router-link>
                    </div>

                    <div v-if="adminItems.length > 0" class="border-t border-gray-200 my-4 mx-2"></div>

                    <div v-if="adminItems.length > 0" class="space-y-1">
                        <div class="px-4 py-2">
                            <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Administração
                            </h3>
                        </div>
                        <template v-for="item in adminItems" :key="item.name">
                            <div v-if="item.children" class="space-y-1">
                                <button type="button"
                                    class="sidebar-link sidebar-link-inactive group w-full"
                                    :aria-expanded="dashboardMenuOpen"
                                    aria-controls="dashboard-submenu"
                                    @click="dashboardMenuOpen = !dashboardMenuOpen">
                                    <component :is="item.icon" class="w-5 h-5 mr-3 flex-shrink-0" />
                                    <span class="flex-1 text-left">{{ item.label }}</span>
                                    <svg class="h-4 w-4 text-gray-400 transition-transform duration-200"
                                        :class="dashboardMenuOpen ? 'rotate-180' : ''" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24" aria-hidden="true">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                <div v-show="dashboardMenuOpen" id="dashboard-submenu" class="space-y-1">
                                    <router-link v-for="child in item.children" :key="child.name" :to="child.to" :class="[
                                        'sidebar-link group ml-5',
                                        isActiveRoute(child.name) ? 'sidebar-link-active' : 'sidebar-link-inactive'
                                    ]" @click="sidebarOpen = false">
                                        <span class="ml-3 truncate">{{ child.label }}</span>
                                    </router-link>
                                </div>
                            </div>
                            <router-link v-else :to="item.to" :class="[
                                'sidebar-link group',
                                isActiveRoute(item.name) ? 'sidebar-link-active' : 'sidebar-link-inactive'
                            ]" @click="sidebarOpen = false">
                                <component :is="item.icon" class="w-5 h-5 mr-3 flex-shrink-0" />
                                <span class="truncate">{{ item.label }}</span>
                            </router-link>
                        </template>
                    </div>
                </div>

                <div class="border-t border-gray-200 p-4">
                    <div class="flex items-center">
                        <div class="flex-shrink-0">
                            <div class="w-8 h-8 bg-pdca-100 rounded-full flex items-center justify-center">
                                <span class="text-sm font-medium text-pdca-700">
                                    {{ userInitials }}
                                </span>
                            </div>
                        </div>
                        <div class="ml-3 flex-1 min-w-0">
                            <p class="text-sm font-medium text-gray-900 truncate">
                                {{ sessaoStore.usuario?.nome || sessaoStore.usuario?.email }}
                            </p>
                            <p v-if="sessaoStore.usuario?.cargoNome" class="text-xs text-gray-500 truncate">
                                {{ sessaoStore.usuario.cargoNome }}
                            </p>
                            <p class="text-xs text-gray-500 truncate">
                                {{ sessaoStore.usuario?.clienteNome || 'Cliente' }}
                            </p>
                        </div>
                    </div>
                    <BaseButton variant="secondary" @click="handleLogout" class="mt-3 w-full text-sm">
                        Sair
                    </BaseButton>
                </div>
            </div>
        </nav>

        <div :class="['lg:pl-64 xl:pl-72 min-h-screen flex flex-col', isHomolog ? 'pt-8' : '']">
            <header :class="['sticky z-10 bg-white shadow-sm border-b border-gray-200', isHomolog ? 'top-8' : 'top-0']">
                <div class="flex items-center justify-between h-16 px-4 lg:px-6">
                    <button @click="sidebarOpen = true"
                        class="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                        aria-label="Abrir menu">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>

                    <div class="flex items-center space-x-4">
                        <h2 class="text-lg font-semibold text-gray-900">
                            {{ pageTitle }}
                        </h2>
                    </div>

                    <div class="flex items-center space-x-4">
                    </div>
                </div>
            </header>

            <main class="flex-1 p-4 lg:p-6 xl:p-8 overflow-visible">
                <div class="w-full overflow-visible">
                    <slot />
                </div>
            </main>

            <!-- Rodapé com Versão -->
            <footer class="border-t border-gray-200 bg-white py-3 px-4 lg:px-6">
                <div class="flex items-center justify-center text-xs text-gray-500">
                    <span>PDCA Engenharia © 2026</span>
                    <span class="mx-2">•</span>
                    <span class="font-medium">{{ appVersion }}</span>
                </div>
            </footer>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, h, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSessaoStore } from '@/stores/sessao'
import { useToast } from '@/composables/useToast'
import { BaseButton } from '@/components/base'
import { PapelUsuario } from '@/types/enums'
import { APP_VERSION, isHomologacao } from '@/config/app'

const FolderOpenIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' })
])

const ProjectsIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' })
])

const UsersIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z' })
])

const FolderIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' })
])

const BuildingIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v.01M9 12v.01M9 15v.01M9 18v.01' })
])

const MailIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' })
])

const BriefcaseIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M20 7h-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v2H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2zM10 5h4v2h-4V5zm0 6h4' })
])

const ClipboardDocumentListIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' })
])

const ChartBarIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' })
])

const ComputerDesktopIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('rect', { x: '2', y: '3', width: '20', height: '14', rx: '2' }),
    h('line', { x1: '8', y1: '21', x2: '16', y2: '21' }),
    h('line', { x1: '12', y1: '17', x2: '12', y2: '21' })
])

const DashboardIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M3 3h7v9H3zM14 3h7v5h-7zM14 12h7v9h-7zM3 16h7v5H3z' })
])

const AuditIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' })
])

const route = useRoute()
const router = useRouter()
const sessaoStore = useSessaoStore()
const toast = useToast()

const sidebarOpen = ref(false)
const dashboardMenuOpen = ref(['AdminDashboard', 'AdminUsoPortal'].includes(route.name as string))
const isHomolog = computed(() => isHomologacao())
const appVersion = computed(() => APP_VERSION)

const pageTitle = computed(() => {
    const titles: Record<string, string> = {
        'Home': 'Meus Projetos',
        'ProjetoDetalhe': 'Projeto',
        'Perfil': 'Meu Perfil',
        'AdminDashboard': 'Dashboard',
        'AdminUsoPortal': 'Uso do Portal',
        'AdminClientes': 'Clientes',
        'AdminUsuarios': 'Usuários',
        'AdminProjetos': 'Projetos',
        'AdminConvites': 'Convites',
        'AdminAuditoria': 'Auditoria',
        'AdminMonitor': 'Painel de Sincronização'
    }

    return titles[route.name as string] || route.meta.title || 'Portal do Cliente'
})

const userInitials = computed(() => {
    const usuario = sessaoStore.usuario
    if (!usuario) return '??'

    if (usuario.nome) {
        return usuario.nome
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2)
    }

    return usuario.email.slice(0, 2).toUpperCase()
})

const UserProfileIcon = () => h('svg', {
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round'
}, [
    h('path', { d: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' })
])

const mainNavigationItems = computed(() => {
    return [
        {
            name: 'Home',
            label: 'Meus Projetos',
            to: { name: 'Home' },
            icon: FolderOpenIcon
        },
        {
            name: 'Perfil',
            label: 'Meu Perfil',
            to: { name: 'Perfil' },
            icon: UserProfileIcon
        }
    ]
})

const adminItems = computed(() => {
    const items: any[] = []

    if (sessaoStore.usuario?.papel === PapelUsuario.ADMIN || sessaoStore.usuario?.papel === PapelUsuario.VISUALIZADOR) {
        items.push({
            name: 'AdminDashboardGroup',
            label: 'Dashboard',
            icon: DashboardIcon,
            children: [
                { name: 'AdminDashboard', label: 'Visão Geral', to: { name: 'AdminDashboard' } },
                { name: 'AdminUsoPortal', label: 'Uso do Portal', to: { name: 'AdminUsoPortal' } }
            ]
        })
        items.push({
            name: 'AdminClientes',
            label: 'Clientes',
            to: { name: 'AdminClientes' },
            icon: BuildingIcon
        })
        items.push({
            name: 'AdminProjetos',
            label: 'Projetos',
            to: { name: 'AdminProjetos' },
            icon: FolderIcon
        })
        items.push({
            name: 'AdminCargos',
            label: 'Cargos',
            to: { name: 'AdminCargos' },
            icon: BriefcaseIcon
        })
        items.push({
            name: 'AdminUsuarios',
            label: 'Usuários',
            to: { name: 'AdminUsuarios' },
            icon: UsersIcon
        })
        items.push({
            name: 'AdminConvites',
            label: 'Convites',
            to: { name: 'AdminConvites' },
            icon: MailIcon
        })
        
        items.push({
            name: 'AdminMonitor',
            label: 'Sincronização',
            to: { name: 'AdminMonitor' },
            icon: ComputerDesktopIcon
        })

        if (sessaoStore.usuario?.papel === PapelUsuario.ADMIN) {
            items.push({
                name: 'AdminAuditoria',
                label: 'Auditoria',
                to: { name: 'AdminAuditoria' },
                icon: ClipboardDocumentListIcon
            })
        }
    }

    return items
})

function isActiveRoute(routeName: string): boolean {
    return route.name === routeName
}

watch(() => route.name, (routeName) => {
    if (routeName === 'AdminDashboard' || routeName === 'AdminUsoPortal') {
        dashboardMenuOpen.value = true
    }
})

function handleLogout() {
    try {
        sessaoStore.logout()
        toast.info('Logout realizado', 'Sessão encerrada')
        setTimeout(() => {
            window.location.href = '/login'
        }, 500)
    } catch (error) {
        console.error('Error signing out:', error)
        toast.error('Erro ao fazer logout')
        window.location.href = '/login'
    }
}
</script>

<style scoped>
.sidebar-link {
    @apply flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-200;
}

.sidebar-link-active {
    @apply bg-pdca-100 text-pdca-900;
}

.sidebar-link-inactive {
    @apply text-gray-600 hover:bg-gray-50 hover:text-gray-900;
}
</style>
